// Façade stucco, grime and wall materials.
import { PI } from '../lib/math.js';
import { rnd, rr } from '../lib/random.js';
import { M, canvasTex } from '../lib/factory.js';

// painted stucco (a white multiplier under each wall colour): soft trowel clouds, brush strokes in loose diagonals,
// a few hairline cracks and faint vertical rain streaks; every mark wraps so the tile repeats without seams
export const stucco = canvasTex(256, 256, (c) => {
  c.fillStyle = '#fff';
  c.fillRect(0, 0, 256, 256);
  const wrap = (x, y, r, fn) => {
    for (const ox of [-256, 0, 256])
      for (const oy of [-256, 0, 256])
        if (x + ox + r > 0 && x + ox - r < 256 && y + oy + r > 0 && y + oy - r < 256)
          fn(x + ox, y + oy);
  };
  for (let k = 0; k < 30; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256,
      r = rr(30, 80),
      a = rr(0.05, 0.12);
    wrap(x0, y0, r, (x, y) => {
      const g = c.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(150,135,115,${a})`);
      g.addColorStop(1, 'rgba(150,135,115,0)');
      c.fillStyle = g;
      c.fillRect(x - r, y - r, 2 * r, 2 * r);
    });
  }
  c.lineCap = 'round';
  for (let k = 0; k < 180; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256,
      len = rr(8, 22),
      ang = rr(-0.9, -0.5) + (rnd() < 0.5 ? 0 : PI / 2),
      lt = rnd() < 0.5,
      w = rr(2, 5);
    wrap(x0, y0, len, (x, y) => {
      c.strokeStyle = lt ? 'rgba(255,255,250,.35)' : 'rgba(165,150,128,.13)';
      c.lineWidth = w;
      c.beginPath();
      c.moveTo(x, y);
      c.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
      c.stroke();
    });
  }
  for (let k = 0; k < 3; k++) {
    const x1 = rnd() * 256,
      y1 = rnd() * 256;
    wrap(x1, y1, 40, (x0, y0) => {
      let x = x0,
        y = y0;
      c.strokeStyle = 'rgba(110,95,78,.28)';
      c.lineWidth = 0.8;
      c.beginPath();
      c.moveTo(x, y);
      for (let s = 0; s < 6; s++) {
        x += rr(-6, 6);
        y += rr(3, 8);
        c.lineTo(x, y);
      }
      c.stroke();
    });
  }
  for (let k = 0; k < 10; k++) {
    const x0 = rnd() * 256,
      y0 = rnd() * 256,
      h = rr(30, 90),
      g = c.createLinearGradient(0, y0, 0, y0 + h);
    g.addColorStop(0, 'rgba(120,110,95,.1)');
    g.addColorStop(1, 'rgba(120,110,95,0)');
    c.fillStyle = g;
    wrap(x0, y0 + h / 2, h, (x, y) => c.fillRect(x - 1.5, y - h / 2, rr(2, 5), h));
  }
});
stucco.repeat.set(0.15, 0.15);
// darker, streaky grime towards the street, like real city façades
export function grime(mat) {
  mat.onBeforeCompile = (sh) => {
    sh.vertexShader =
      'varying vec3 vWPos;\n' +
      sh.vertexShader.replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      );
    sh.fragmentShader =
      'varying vec3 vWPos;\n' +
      sh.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
      float base = mix(.74, 1.0, smoothstep(.3, 2.6, vWPos.y));
      float streak = .94 + .06 * sin(vWPos.x * 9.1 + sin(vWPos.z * 2.3) * 3.) * sin(vWPos.z * 8.3 + vWPos.x * .7);
      diffuseColor.rgb *= base * streak;`,
      );
  };
  return mat;
}
export const wallMats = [
  0xe8c99a, 0xe3b98e, 0xd6a985, 0xefd8b0, 0xd9a676, 0xe7c3a8, 0xd29a82, 0xc9c7a6,
].map((c) => grime(M(c, { map: stucco, flatShading: false })));
export const rustTex = canvasTex(64, 64, (c) => {
  c.fillStyle = '#b9ab95';
  c.fillRect(0, 0, 64, 64);
  c.fillStyle = '#8f8270';
  for (let y = 0; y < 64; y += 16) {
    c.fillRect(0, y, 64, 2);
    for (let x = (y / 16) % 2 ? 0 : 16; x < 64; x += 32) c.fillRect(x, y, 2, 16);
  }
  for (let k = 0; k < 400; k++) {
    c.fillStyle = rnd() < 0.5 ? '#a99b85' : '#c6b9a3';
    c.fillRect(rnd() * 64, rnd() * 64, 1.5, 1.5);
  }
});
rustTex.repeat.set(1 / 1.6, 1 / 0.4);
